<template>
    <ExportWrapper :file-name="fileName">
        <ExportConditionStatusTitleSection />
        <div v-if="!!componentStatusData?.isNoActionSet" class="section-wrapper">
            <ConditionStatusHeaderNoActionCategoryButton :is-no-action-set="!!componentStatusData?.isNoActionSet" is-export-layout />
        </div>
        <div class="master-data-wrapper">
            <MasterDataPanel :transformer-id="transformerId" />
        </div>
        <div class="table-wrapper">
            <ConditionStatusLatestReportsSection is-export-layout :component-id="transformerId" :component-type="ComponentType.Transformer" />
        </div>
        <div v-if="userPermissions.canViewTransformerRecommendations" class="section-wrapper">
            <ExportConditionStatusRecommendationsSection :component-id="transformerId" :preload-size="recommendationsPreloadSize" />
        </div>
        <template v-if="!isSectionHidden">
            <div class="table-wrapper">
                <ConditionStatusAnalysisTable
                    :analysis-type="AnalysisType.GasInOil"
                    :report-display-count="MAX_REPORT_COUNT"
                />
            </div>
            <div class="table-wrapper">
                <ConditionStatusAnalysisTable
                    :analysis-type="AnalysisType.Furans"
                    :report-display-count="MAX_REPORT_COUNT"
                />
            </div>
            <div class="table-wrapper">
                <ConditionStatusAnalysisTable
                    :analysis-type="AnalysisType.InsulatingOil"
                    :report-display-count="MAX_REPORT_COUNT"
                />
            </div>
            <div class="section-wrapper">
                <ExportConditionStatusAnalysisCommentsSection :component-id="transformerId" :component-type="ComponentType.Transformer" :preload-size="analysisCommentsPreloadSize" />
            </div>
            <div class="chart-wrapper">
                <ExportConditionStatusHistoricalTrendsSection />
            </div>
        </template>
        <div class="section-wrapper">
            <ExportConditionStatusVisualInspectionSection />
        </div>
        <div class="section-wrapper">
            <ExportConditionStatusComponentCommentsSection />
        </div>
        <ExportFooterSection />
    </ExportWrapper>
</template>

<script setup lang="ts">
import { provideTransformerId } from '@/composables/useTransformerId'
import { computed, toRefs } from 'vue'
import ExportWrapper from '@/pages/exports/ExportWrapper.vue'
import ExportConditionStatusTitleSection from '@/pages/exports/transformers/condition-status/sections/ExportConditionStatusTitleSection.vue'
import ConditionStatusLatestReportsSection from '@/pages/common/component-status/ConditionStatusLatestReportsSection.vue'
import MasterDataPanel from '@/pages/transformers/condition-status/sections/master-data/MasterDataPanel.vue'
import ConditionStatusAnalysisTable from '@/pages/transformers/condition-status/sections/analyses/ConditionStatusAnalysisTable.vue'
import { AnalysisType } from '@/configuration/analysis/analysis-types'
import { useRoute } from 'vue-router'
import ExportFooterSection from '@/pages/exports/common/ExportFooterSection.vue'
import ExportConditionStatusRecommendationsSection from '@/pages/exports/transformers/condition-status/sections/ExportConditionStatusRecommendationsSection.vue'
import ExportConditionStatusAnalysisCommentsSection from '@/pages/exports/transformers/condition-status/sections/ExportConditionStatusAnalysisCommentsSection.vue'
import ExportConditionStatusVisualInspectionSection from '@/pages/exports/transformers/condition-status/sections/ExportConditionStatusVisualInspectionSection.vue'
import { useTransformerDetailsQuery } from '@/api/queries/transformers/useTransformerDetailsQuery'
import { DATE_FORMAT } from '@/utils/date-utils'
import { format } from 'date-fns'
import { CONSTRUCTION_TYPE } from '@/utils/master-data-utils'
import ExportConditionStatusComponentCommentsSection from '@/pages/exports/transformers/condition-status/sections/ExportConditionStatusComponentCommentsSection.vue'
import { useComponentStatusQuery } from '@/api/queries/general/useComponentStatusQuery'
import ConditionStatusHeaderNoActionCategoryButton from '@/pages/transformers/condition-status/sections/no-action-category/ConditionStatusHeaderNoActionCategoryButton.vue'
import { ComponentType } from '@/configuration/component/component-types'
import ExportConditionStatusHistoricalTrendsSection from '@/pages/exports/transformers/condition-status/sections/ExportConditionStatusHistoricalTrendsSection.vue'
import { usePermissions } from '@/composables/usePermissions'

const props = defineProps<{
    transformerId: string
}>()

const MAX_REPORT_COUNT = 3

const { transformerId } = toRefs(props)

const { query } = useRoute()
const { userPermissions } = usePermissions()

const { data: transformerDetailsData } = useTransformerDetailsQuery(transformerId)
const { data: componentStatusData } = useComponentStatusQuery(props.transformerId, ComponentType.Transformer)

const isSectionHidden = computed(() => {
    return transformerDetailsData.value?.transformerTypeEnumSid === CONSTRUCTION_TYPE.TROCKEN
})

const fileName = computed(() => {
    if (!transformerDetailsData.value) {
        return undefined
    }

    const plantGroupName = transformerDetailsData.value.plantGroupName
    const plantName = transformerDetailsData.value.plantName
    const transformerSid = transformerDetailsData.value.transformerSid
    const currentDate = format(new Date(), DATE_FORMAT)

    return `HCA_ComponentStatus_${plantGroupName}_${plantName}_${transformerSid}_${currentDate}`
})

const recommendationsPreloadSize = computed(() => {
    const size = parseInt(query.recommendationsLength as string)

    return isNaN(size) ? undefined : size
})

const analysisCommentsPreloadSize = computed(() => {
    const size = parseInt(query.analysisCommentsLength as string)

    return isNaN(size) ? undefined : size
})

provideTransformerId(transformerId)
</script>

<style scoped lang="scss">
:deep(.content-panel) {
    border: 1px solid $pbl-divider;
    box-shadow: unset;
}

.section-wrapper {
    padding: $spacing-xs $spacing-s;
}

.master-data-wrapper {
    padding: $spacing-xs $spacing-s;
}

.table-wrapper {
    break-inside: avoid;
    padding: $spacing-xs $spacing-s;

    :deep(.analysis-table) {
        margin-top: 8px;
    }
}

.chart-wrapper {
    break-inside: avoid;
    padding: $spacing-xs $spacing-s;
}
</style>
